// v2.15.0 — Modale v3 d'ajout/édition d'adresse (nouveau format CIV-VILLE3-COMMUNE3-XXXXX).
// - Mapbox 3D (fallback Leaflet) avec marker draggable. lat/lng cachés à l'utilisateur.
// - Sélecteurs Ville/Commune dépendants. Suffix manuel ou bouton 🎲.
// - Check name-availability au blur. Boutons suggestions en chips cliquables.

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MiniMapPreview from "@/components/MiniMapPreview";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SUFFIX_REGEX = /^[A-Z0-9]{0,5}$/;
// v2.15.1 — Regex permissif pour parser un code CIV collé (WhatsApp etc.).
const CIV_PASTE_REGEX = /CIV-([A-Z]{2,4})-([A-Z]{2,4})-([A-Z0-9-]+)/i;

export default function AddressFormModalV3({ open, userId, onClose, onCreated, preferIsHome = false }) {
  const [cities, setCities] = useState([]);
  const [cityCode, setCityCode] = useState("ABJ");
  const [communeCode, setCommuneCode] = useState("YOP");
  const [suffix, setSuffix] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isHome, setIsHome] = useState(preferIsHome);
  const [coords, setCoords] = useState(null); // {lat, lng}
  const [nameStatus, setNameStatus] = useState(null); // {available, suggestions?}
  const [submitting, setSubmitting] = useState(false);
  const nameDebounceRef = useRef(null);

  // Mount: fetch cities + reset state.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/v3/cities`).then((r) => {
      const list = r.data.cities || [];
      setCities(list);
    }).catch(() => setCities([]));
    setName(""); setDescription(""); setSuffix(""); setNameStatus(null); setCoords(null);
    setCityCode("ABJ"); setCommuneCode("YOP"); setIsHome(preferIsHome);
  }, [open, preferIsHome]);

  const cityObj = useMemo(() => cities.find((c) => c.code === cityCode) || null, [cities, cityCode]);
  const communes = cityObj?.communes || [];
  const communeObj = useMemo(() => communes.find((c) => c.code === communeCode) || null, [communes, communeCode]);

  // When city changes → reset commune to first available and recenter map.
  useEffect(() => {
    if (!cityObj) return;
    if (!communes.find((c) => c.code === communeCode)) {
      setCommuneCode(communes[0]?.code || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityCode]);

  // When commune changes → recenter the mini-map preview if no coords set yet.
  useEffect(() => {
    if (!communeObj) return;
    if (!coords) {
      // Pré-positionner sur le centroïde de la commune sans le marquer "confirmé".
    }
  }, [communeObj, coords]);

  const preview = useMemo(() => {
    if (cityCode && communeCode && suffix.length === 5) {
      return `CIV-${cityCode}-${communeCode}-${suffix}`;
    }
    return `CIV-${cityCode || "···"}-${communeCode || "···"}-${(suffix || "").padEnd(5, "·")}`;
  }, [cityCode, communeCode, suffix]);

  // v2.15.0a — Suffix DÉTERMINISTE : recalculé à chaque changement de (coords, commune).
  // L'utilisateur n'a pas accès au bouton 🎲 (supprimé). Input toujours read-only.
  useEffect(() => {
    if (!coords || !communeCode) return;
    let cancelled = false;
    axios.post(`${API}/v3/addresses/encode-suffix`, {
      commune_code: communeCode, lat: coords.lat, lng: coords.lng,
    }).then((r) => {
      if (cancelled) return;
      if (r.data?.ok) setSuffix(r.data.suffix);
    }).catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, [coords, communeCode]);

  const checkNameAvailability = (val) => {
    if (!val || !val.trim()) { setNameStatus(null); return; }
    axios.get(`${API}/v3/addresses/name-availability`, { params: { name: val } })
      .then((r) => setNameStatus(r.data))
      .catch(() => setNameStatus(null));
  };

  const onNameChange = (e) => {
    const v = e.target.value;
    // v2.15.1 — Détecte un code CIV collé et pré-remplit ville/commune/suffix.
    if (tryParseAndApplyCivCode(v)) return;
    setName(v);
    setNameStatus(null);
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => checkNameAvailability(v), 500);
  };

  const onDescriptionChange = (e) => {
    const v = e.target.value;
    // v2.15.1 — Détecte un code CIV collé dans la description aussi.
    if (tryParseAndApplyCivCode(v, /* preserveDescription */ true)) return;
    setDescription(v);
  };

  // v2.15.1 + v2.15.0a — Parser réutilisable : si le texte contient un code CIV, applique-le
  // aux selects + DÉCODE le suffix → place le pin sur la carte (Flux B).
  const tryParseAndApplyCivCode = (text, preserveDescription = false) => {
    if (!text) return false;
    const m = String(text).match(CIV_PASTE_REGEX);
    if (!m) return false;
    const city = m[1].toUpperCase();
    const commune = m[2].toUpperCase();
    let suffixRaw = m[3].toUpperCase();
    // Si suffix > 5 chars (ancien format CIV-XXX-YYY-001-500) → garde 5 derniers alphanumeric.
    const suffixCompact = suffixRaw.replace(/[^A-Z0-9]/g, '').slice(-5);
    // Cherche dans la liste des villes/communes connues.
    const cityFound = cities.find((c) => c.code === city);
    const fallbackCity = cityFound || cities.find((c) => (c.communes || []).some((co) => co.code === commune || co.code === city));
    if (!fallbackCity) {
      toast.error(`Ville ${city} inconnue dans le registre — collage ignoré`);
      return false;
    }
    const targetCity = fallbackCity.code;
    const targetCommune = (fallbackCity.communes || []).find((co) => co.code === commune)
      || (fallbackCity.communes || []).find((co) => co.code === city)
      || fallbackCity.communes?.[0];
    const targetCommuneCode = targetCommune?.code || commune;
    setCityCode(targetCity);
    setCommuneCode(targetCommuneCode);
    setSuffix(suffixCompact);
    if (!preserveDescription) {
      setName("");
      setNameStatus(null);
    }

    // v2.15.0a — Flux B : décode le suffix → place le pin sur la carte.
    if (suffixCompact.length === 5) {
      axios.post(`${API}/v3/addresses/decode-suffix`, {
        commune_code: targetCommuneCode, suffix: suffixCompact,
      }).then((r) => {
        if (r.data?.ok) {
          setCoords({ lat: r.data.lat, lng: r.data.lng });
          toast.success(`📥 Code CIV décodé — pin placé (±${r.data.precision_m}m)`);
        } else {
          toast.success("📥 Code CIV détecté et pré-rempli !");
        }
      }).catch(() => {
        toast.success("📥 Code CIV détecté et pré-rempli !");
      });
    } else {
      toast.success("📥 Code CIV détecté et pré-rempli !");
    }
    return true;
  };

  // v2.15.0a — Suffix DÉTERMINISTE : pas de modification manuelle, pas de random.
  // L'input est read-only. Le suffix est recalculé automatiquement via le useEffect ci-dessus.

  const onMapPin = (lat, lng) => setCoords({ lat, lng });

  const canSubmit = !!(name.trim() && cityCode && communeCode && suffix.length === 5 && coords && nameStatus?.available !== false);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) { toast.error("Remplis tous les champs requis"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/v3/addresses`, {
        user_id: userId,
        city_code: cityCode,
        commune_code: communeCode,
        suffix,
        name: name.trim(),
        description: description.trim() || null,
        lat: coords.lat, lng: coords.lng,
        is_home: !!isHome,
      });
      toast.success(`✅ Adresse ${r.data.address.address_civ} créée`);
      onCreated && onCreated(r.data.address);
      onClose();
    } catch (e) {
      const reason = e.response?.data?.reason;
      if (reason === "name_taken") {
        setNameStatus({ available: false, suggestions: e.response.data.suggestions });
        toast.error("Nom déjà pris — choisis une suggestion");
      } else if (reason === "suffix_taken") {
        toast.error("Ce point est déjà pris — déplace légèrement le marqueur puis réessaie");
      } else if (reason === "invalid_suffix") {
        toast.error("Suffix invalide — doit contenir ≥1 chiffre ET ≥1 lettre");
      } else {
        toast.error(reason || e.message);
      }
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  const mapLat = coords?.lat ?? communeObj?.lat;
  const mapLng = coords?.lng ?? communeObj?.lng;

  return (
    <div data-testid="address-form-modal-v3"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}
        style={{ background: "#0f172a", color: "#F1F5F9", padding: 22, borderRadius: 14, maxWidth: 560, width: "100%", maxHeight: "92vh", overflowY: "auto", border: "1px solid rgba(255,255,255,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>➕ Nouvelle adresse</h2>
          <button type="button" data-testid="addr-v3-close" onClick={onClose} style={{ background: "rgba(255,255,255,0.08)", border: "none", color: "#fff", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>

        <label style={{ display: "block", marginBottom: 4, fontSize: 12, color: "#94A3B8" }}>NOM *</label>
        <input data-testid="addr-v3-name-input" type="text" value={name} onChange={onNameChange}
          placeholder="Ex: Bureau, Chez Maman" required
          style={{ width: "100%", padding: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 13 }} />
        {nameStatus && nameStatus.available === true && <small data-testid="addr-v3-name-ok" style={{ color: "#22C55E" }}>✓ disponible</small>}
        {nameStatus && nameStatus.available === false && (
          <div data-testid="addr-v3-name-taken" style={{ fontSize: 11, color: "#F87171", marginTop: 4 }}>
            ✗ pris — essaie :
            {(nameStatus.suggestions || []).map((s, i) => (
              <button key={i} type="button" data-testid={`addr-v3-suggest-${i}`}
                onClick={() => { setName(s); checkNameAvailability(s); }}
                style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 999, background: "rgba(248,113,113,0.18)", border: "1px solid #F87171", color: "#FECACA", cursor: "pointer", fontSize: 11 }}>{s}</button>
            ))}
          </div>
        )}

        <label style={{ display: "block", marginTop: 12, marginBottom: 4, fontSize: 12, color: "#94A3B8" }}>DESCRIPTION (optionnel)</label>
        <input data-testid="addr-v3-desc-input" type="text" value={description} onChange={onDescriptionChange}
          placeholder="Ex: Immeuble Le Mans, 3e étage"
          style={{ width: "100%", padding: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 13 }} />

        <label style={{ display: "block", marginTop: 12, marginBottom: 4, fontSize: 12, color: "#94A3B8" }}>CODE ADRESSE *</label>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr auto auto", gap: 6, alignItems: "center" }}>
          <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#93C5FD" }}>CIV-</span>
          <select data-testid="addr-v3-city-select" value={cityCode} onChange={(e) => setCityCode(e.target.value)}
            style={{ padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12 }}>
            {cities.map((c) => <option key={c.code} value={c.code} style={{ background: "#0f172a" }}>{c.code} · {c.name}</option>)}
          </select>
          <select data-testid="addr-v3-commune-select" value={communeCode} onChange={(e) => setCommuneCode(e.target.value)}
            style={{ padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12 }}>
            {communes.map((c) => <option key={c.code} value={c.code} style={{ background: "#0f172a" }}>{c.code} · {c.name}</option>)}
          </select>
          <input data-testid="addr-v3-suffix-input" type="text" value={suffix} readOnly placeholder="·····" maxLength={5}
            title="Calculé automatiquement depuis la position GPS"
            style={{ width: 80, padding: 6, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", color: "#93C5FD", borderRadius: 4, fontSize: 13, fontFamily: "monospace", textAlign: "center", cursor: "not-allowed", letterSpacing: 1 }} />
          <span data-testid="addr-v3-suffix-lock" title="Le suffix est calculé automatiquement par encodage géométrique (GeoCodec). Pas modifiable."
            style={{ padding: "6px 8px", background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.3)", color: "#93C5FD", borderRadius: 4, fontSize: 14, display: "inline-flex", alignItems: "center" }}>🔒</span>
        </div>
        <div data-testid="addr-v3-preview" style={{ marginTop: 6, fontSize: 12, color: "#94A3B8" }}>
          Aperçu : <code style={{ color: "#F1F5F9", fontWeight: 700, background: "rgba(59,130,246,0.15)", padding: "2px 6px", borderRadius: 4 }}>{preview}</code>
        </div>

        <label style={{ display: "block", marginTop: 14, marginBottom: 4, fontSize: 12, color: "#94A3B8" }}>🌍 Position sur la carte (clique pour pin)</label>
        <MiniMapPreview lat={mapLat} lng={mapLng} onSet={onMapPin} height={200} />
        <div data-testid="addr-v3-coords-status" style={{ marginTop: 4, fontSize: 11, color: coords ? "#22C55E" : "#94A3B8" }}>
          {coords ? "📍 Position confirmée sur la carte" : "🌍 Clique sur la carte pour pin la position"}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 13, cursor: "pointer" }}>
          <input data-testid="addr-v3-ishome-checkbox" type="checkbox" checked={isHome} onChange={(e) => setIsHome(e.target.checked)} />
          <span>Définir comme mon <strong>Domicile principal</strong></span>
        </label>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button type="button" data-testid="addr-v3-cancel-btn" onClick={onClose} style={{ padding: "8px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#cbd5e1", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Annuler</button>
          <button type="submit" data-testid="addr-v3-submit-btn" disabled={!canSubmit || submitting}
            style={{ padding: "8px 18px", background: canSubmit ? "#16A34A" : "rgba(255,255,255,0.06)", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: canSubmit ? "pointer" : "not-allowed" }}>
            {submitting ? "..." : "Ajouter"}
          </button>
        </div>
      </form>
    </div>
  );
}
